import React from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import { X } from './icons';
import { setDraggedVariable } from './dragState';
/**
 * VariableTagView — React NodeView for the inline variable pill.
 * Renders as a styled <<Variable Name>> chip that is non-editable,
 * supports internal drag-and-drop movement, and has a hover delete cross mark.
 */
export function VariableTagView({ node, getPos, deleteNode, editor, selected }) {
    const { label, variableKey } = node.attrs;
    const handleDragStart = (e) => {
        const fromPos = typeof getPos === 'function' ? getPos() : undefined;
        const dragData = {
            key: variableKey || label,
            label,
            fromPos,
            isInternal: true,
            marks: (node?.marks && Array.isArray(node.marks))
                ? node.marks.map((m) => ({ type: m.type.name, attrs: m.attrs }))
                : [],
        };
        setDraggedVariable(dragData);
        document.body.classList.add('is-dragging-variable');
        try {
            e.dataTransfer.setData('application/notice-variable', JSON.stringify(dragData));
            e.dataTransfer.setData('text/plain', `<<${label}>>`);
            e.dataTransfer.effectAllowed = 'move';
        }
        catch {
            // ignore
        }
        // Create drag ghost
        try {
            const ghost = document.createElement('div');
            ghost.className = 'drag-ghost';
            ghost.textContent = `<<${label}>>`;
            document.body.appendChild(ghost);
            e.dataTransfer.setDragImage(ghost, 0, 0);
            requestAnimationFrame(() => {
                ghost.remove();
            });
        }
        catch {
            // ignore
        }
    };
    const handleDragEnd = () => {
        document.body.classList.remove('is-dragging-variable');
        setTimeout(() => {
            setDraggedVariable(null);
        }, 150);
    };
    const handleDelete = (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (typeof deleteNode === 'function') {
            deleteNode();
        }
        else if (typeof getPos === 'function' && editor) {
            const pos = getPos();
            if (typeof pos === 'number') {
                editor.commands.deleteRange({ from: pos, to: pos + node.nodeSize });
            }
        }
    };
    const handlePillClick = (e) => {
        if (e.target.closest('.variable-tag-delete-btn')) {
            return;
        }
        e.stopPropagation();
        e.preventDefault();
        if (editor && !editor.isDestroyed && typeof getPos === 'function') {
            const pos = getPos();
            if (typeof pos === 'number') {
                editor.chain().focus().setNodeSelection(pos).run();
            }
        }
    };

    const isNodeSelected = selected || (() => {
        if (!editor || typeof getPos !== 'function') return false;
        const pos = getPos();
        if (typeof pos !== 'number') return false;
        const sel = editor.state?.selection;
        if (!sel) return false;
        return sel.from === pos && sel.to === pos + node.nodeSize;
    })();

    return (
      <NodeViewWrapper
        as="span"
        className={`variable-tag-wrapper ${isNodeSelected ? 'ProseMirror-selectednode is-selected-in-range is-selected' : ''}`}
        data-drag-handle
        onClick={handlePillClick}
      >
        <span
          className="variable-tag-pill"
          contentEditable={false}
          draggable={true}
          data-drag-handle
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onClick={handlePillClick}
          title={`Variable: <<${label}>> (Click to select, drag to move)`}
        >
          <span className="variable-tag-icon" title="Drag to move">⟐</span>
          <span className="variable-tag-label">{label}</span>
          <button
            type="button"
            className="variable-tag-delete-btn"
            draggable={false}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={handleDelete}
            title={`Remove <<${label}>>`}
            aria-label={`Remove <<${label}>>`}
          >
            <X size={10} strokeWidth={2.5}/>
          </button>
        </span>
      </NodeViewWrapper>
    );
}

